import { useCallback, useRef } from "react";

type RequestSeqMap = Record<string, number>;

type RunLatestOptions<T> = {
  request: () => Promise<T>;
  onSuccess?: (result: T) => void | Promise<void>;
  onError?: (error: unknown) => void | Promise<void>;
  onFinally?: () => void | Promise<void>;
};

export const useLatestRequest = () => {
  const requestSeqByKeyRef = useRef<RequestSeqMap>({});

  const beginRequest = useCallback((key: string) => {
    const nextSeq = (requestSeqByKeyRef.current[key] ?? 0) + 1;
    requestSeqByKeyRef.current[key] = nextSeq;
    return nextSeq;
  }, []);

  const invalidateRequest = useCallback((key: string) => {
    requestSeqByKeyRef.current[key] = (requestSeqByKeyRef.current[key] ?? 0) + 1;
  }, []);

  const isLatestRequest = useCallback((key: string, requestSeq: number) => {
    return requestSeqByKeyRef.current[key] === requestSeq;
  }, []);

  const runLatest = useCallback(
    async <T>(key: string, options: RunLatestOptions<T>) => {
      const requestSeq = beginRequest(key);
      try {
        const result = await options.request();
        if (isLatestRequest(key, requestSeq)) {
          await options.onSuccess?.(result);
        }
      } catch (error) {
        if (isLatestRequest(key, requestSeq)) {
          await options.onError?.(error);
        }
      } finally {
        if (isLatestRequest(key, requestSeq)) {
          await options.onFinally?.();
        }
      }
    },
    [beginRequest, isLatestRequest]
  );

  return {
    beginRequest,
    invalidateRequest,
    isLatestRequest,
    runLatest
  };
};
